import type { User } from '@h2h/shared';
import { Avatar } from '../../components/ui/bits';
import { profileCard } from '../profile/tokens';
import { cn } from '../../lib/cn';

export function PlayerRivalrySection({
  name,
  target,
  me,
  myWins,
  theirWins,
  draws,
  seriesLength,
  hasRivalry
}: {
  name: string;
  target: User;
  me: User | null | undefined;
  myWins: number;
  theirWins: number;
  draws: number;
  seriesLength: number;
  hasRivalry: boolean;
}) {
  const firstName = name.split(/\s+/)[0];
  const drawTail = draws > 0 ? ` · ${draws} drawn` : '';

  let rivalryTop: string;
  let rivalryBottom: string;
  if (!hasRivalry) {
    rivalryTop = 'No duels settled yet';
    rivalryBottom = `Challenge ${firstName} to start the rivalry`;
  } else if (myWins > theirWins) {
    rivalryTop = 'You lead the head-to-head';
    rivalryBottom = `${myWins}–${theirWins}${drawTail}`;
  } else if (myWins < theirWins) {
    rivalryTop = `${firstName} leads the head-to-head`;
    rivalryBottom = `${theirWins}–${myWins}${drawTail}`;
  } else {
    rivalryTop = 'All square';
    rivalryBottom = `${myWins}–${theirWins}${drawTail}`;
  }

  return (
    <div className={cn('mt-4 p-4', profileCard)}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink3">Head-to-head</span>
        <span className="text-[10px] font-bold text-ink3">{hasRivalry ? `${seriesLength} settled` : 'New rivalry'}</span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-4">
        <div className="flex items-center gap-2">
          <Avatar name={me?.displayName ?? 'You'} tone={me?.avatarTone ?? 0} src={me?.photoURL} size={28} />
          <span className="text-[12px] font-bold text-ink2">You</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="num text-[28px] font-extrabold leading-none text-ink">{myWins}</span>
          <span className="text-[14px] font-bold text-ink4">–</span>
          <span className="num text-[28px] font-extrabold leading-none text-accent-ink">{theirWins}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="max-w-[64px] truncate text-[12px] font-bold text-ink">{firstName}</span>
          <Avatar name={name} tone={target.avatarTone ?? 0} src={target.photoURL} size={28} />
        </div>
      </div>
      <div className="mt-3 text-center">
        <div className="text-[11.5px] font-medium text-ink3">{rivalryTop}</div>
        <div className="mt-0.5 text-[13px] font-bold text-ink">{rivalryBottom}</div>
      </div>
    </div>
  );
}
